// AuthContext.tsx
import { createContext, useContext, useMemo, useState, ReactNode, useEffect, useRef } from 'react';
import { apiUrl } from './api/client';
import { store } from './store/store';
import { clearCurrentConversationId, clearConversationList } from './store/conversationsSlice';
import { setUserProfile, clearUserProfile, setUserProfileLoading } from './store/userProfileSlice';
import { UserProfile } from './types/UserProfile';
import { ApiRoutes } from './types/ApiRoutes';

export interface AuthTokens {
  accessToken: string;
  tokenType: string; // typically "Bearer"
  expiresIn: number; // seconds
  // client-side computed absolute expiry time (ms since epoch)
  expiresAt?: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isInitializing: boolean;
  tokens: AuthTokens | null;
  login: (email: string, password: string) => Promise<void>;
  demoLogin: (email?: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  confirmEmail: (userId: string, confirmationCode: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmResetCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  authHeader: () => Record<string, string>;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const STORAGE_KEY = 'auth.tokens.v1';
// Storage strategy: 'session' limits persistence to tab/session and slightly reduces risk surface vs 'local'.
// Switch to 'local' if you want persistence across browser restarts as well.
const STORAGE_MODE: 'session' | 'local' = 'local';

function getStorage() {
  try {
    return STORAGE_MODE === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return undefined;
  }
}

function storageGet(key: string): string | null {
  const s = getStorage();
  if (!s) {
    return null;
  }
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  const s = getStorage();
  if (!s) {
    return;
  }
  try {
    s.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function storageRemove(key: string): void {
  const s = getStorage();
  if (!s) {
    return;
  }
  try {
    s.removeItem(key);
  } catch {
    /* ignore */
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const refreshingRef = useRef<Promise<AuthTokens | null> | null>(null);

  // Initialize tokens from session endpoint on startup
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        // Try to bootstrap session from server cookie
        const res = await fetch(apiUrl(ApiRoutes.Session), {
          method: 'GET',
          credentials: 'include',
        });

        if (res.ok) {
          const sessionData = await res.json();
          const expiresAt = Date.now() + (sessionData.expiresIn ?? 0) * 1000;
          const tokens: AuthTokens = {
            accessToken: sessionData.accessToken,
            tokenType: sessionData.tokenType,
            expiresIn: sessionData.expiresIn,
            expiresAt,
          };
          if (!cancelled) {
            setTokens(tokens);
            storageSet(STORAGE_KEY, JSON.stringify(tokens));
            // Fetch user profile after successful session restoration
            await fetchAndStoreUserProfile(tokens);
          }
        } else {
          // No active session, check local storage as fallback
          const raw = storageGet(STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as AuthTokens;
            if (parsed && parsed.accessToken) {
              if (!cancelled) {
                setTokens(parsed);
                // Try to refresh if near/after expiry
                await ensureValidToken(false);
              }
            }
          }
        }
      } catch {
        // ignore errors during initialization
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cross-tab synchronization: react to login/logout in other tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) {
        return;
      }
      if (typeof e.newValue === 'string') {
        try {
          const parsed = JSON.parse(e.newValue) as AuthTokens;
          if (parsed && typeof parsed.accessToken === 'string' && parsed.accessToken) {
            setTokens(parsed);
          } else {
            setTokens(null);
          }
        } catch {
          setTokens(null);
        }
      } else {
        // key removed
        setTokens(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isAuthenticated = !!tokens?.accessToken;

  const login = async (email: string, password: string) => {
    const res = await fetch(apiUrl(ApiRoutes.Login), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include cookies in request/response
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      // Try to extract error message from server if available
      let message = 'Login failed';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    const data = (await res.json()) as AuthTokens;
    const expiresAt = Date.now() + (data.expiresIn ?? 0) * 1000;
    const withExpiry: AuthTokens = { ...data, expiresAt };
    setTokens(withExpiry);
    storageSet(STORAGE_KEY, JSON.stringify(withExpiry));

    // Fetch user profile after successful login
    await fetchAndStoreUserProfile(withExpiry);
  };

  const demoLogin = async (email?: string) => {
    const res = await fetch(apiUrl(ApiRoutes.Demo), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include cookies in request/response
      body: JSON.stringify(email ? { email } : {}),
    });
    if (!res.ok) {
      let message = 'Demo login failed';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    const data = (await res.json()) as AuthTokens;
    const expiresAt = Date.now() + (data.expiresIn ?? 0) * 1000;
    const withExpiry: AuthTokens = { ...data, expiresAt };
    setTokens(withExpiry);
    storageSet(STORAGE_KEY, JSON.stringify(withExpiry));

    // Fetch user profile after successful demo login
    await fetchAndStoreUserProfile(withExpiry);
  };

  const signup = async (email: string, password: string) => {
    const res = await fetch(apiUrl(ApiRoutes.Signup), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      let message = 'Signup failed';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    // Signup no longer returns tokens, just success message
  };

  const confirmEmail = async (userId: string, confirmationCode: string) => {
    const res = await fetch(apiUrl(ApiRoutes.ConfirmEmail), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include cookies in request/response
      body: JSON.stringify({ userId, confirmationCode }),
    });
    if (!res.ok) {
      let message = 'Email confirmation failed';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    const data = (await res.json()) as AuthTokens;
    const expiresAt = Date.now() + (data.expiresIn ?? 0) * 1000;
    const withExpiry: AuthTokens = { ...data, expiresAt };
    setTokens(withExpiry);
    storageSet(STORAGE_KEY, JSON.stringify(withExpiry));

    // Fetch user profile after successful email confirmation
    await fetchAndStoreUserProfile(withExpiry);
  };

  const forgotPassword = async (email: string) => {
    const res = await fetch(apiUrl(ApiRoutes.ForgotPassword), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      let message = 'Failed to send reset code';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    // Success - message sent
  };

  const confirmResetCode = async (email: string, code: string) => {
    const res = await fetch(apiUrl(ApiRoutes.ConfirmCode), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include cookies in request/response
      body: JSON.stringify({ email, code }),
    });
    if (!res.ok) {
      let message = 'Invalid or expired code';
      try {
        const data = await res.json();
        if (data?.message) {
          message = data.message;
        }
      } catch {
        // ignore
      }
      throw new Error(message);
    }
    const data = (await res.json()) as AuthTokens;
    const expiresAt = Date.now() + (data.expiresIn ?? 0) * 1000;
    const withExpiry: AuthTokens = { ...data, expiresAt };
    setTokens(withExpiry);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(withExpiry));
    } catch {
      // ignore
    }

    // Fetch user profile after successful reset code confirmation
    await fetchAndStoreUserProfile(withExpiry);
  };

  const logout = async () => {
    try {
      // Call server logout endpoint to clear cookie
      await fetch(apiUrl(ApiRoutes.Logout), {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors, clear local state anyway
    }
    setTokens(null);
    storageRemove(STORAGE_KEY);

    // Clear conversation state from Redux
    store.dispatch(clearCurrentConversationId());
    store.dispatch(clearConversationList());
    store.dispatch(clearUserProfile());
  };

  const fetchAndStoreUserProfile = async (authTokens: AuthTokens) => {
    try {
      store.dispatch(setUserProfileLoading(true));
      const headers = new Headers();
      headers.set('Authorization', `${authTokens.tokenType} ${authTokens.accessToken}`);
      const res = await fetch(apiUrl(ApiRoutes.UserProfile), {
        method: 'GET',
        headers,
      });
      if (res.ok) {
        const profile = (await res.json()) as UserProfile;
        store.dispatch(setUserProfile(profile));
      } else {
        store.dispatch(setUserProfileLoading(false));
      }
    } catch {
      store.dispatch(setUserProfileLoading(false));
    }
  };

  const authHeader = useMemo(() => {
    return () => {
      if (tokens?.accessToken) {
        return { Authorization: `${tokens.tokenType} ${tokens.accessToken}` } as Record<
          string,
          string
        >;
      }
      return {} as Record<string, string>;
    };
  }, [tokens]);

  // Attempts to refresh the access token if it's expired or expiring within 60s
  const ensureValidToken = async (forceRefresh = false): Promise<AuthTokens | null> => {
    const now = Date.now();
    const thresholdMs = 60_000; // refresh one minute before expiry
    if (!tokens) {
      return null;
    }
    if (!forceRefresh && tokens.expiresAt && tokens.expiresAt - now > thresholdMs) {
      return tokens; // still valid
    }
    // Deduplicate concurrent refresh calls
    if (refreshingRef.current) {
      return refreshingRef.current;
    }
    const refreshPromise = (async () => {
      try {
        const res = await fetch(apiUrl(ApiRoutes.Refresh), {
          method: 'POST',
          credentials: 'include', // Cookie-based refresh
        });
        if (!res.ok) {
          throw new Error('Refresh failed');
        }
        const data = (await res.json()) as AuthTokens;
        const expiresAt = Date.now() + (data.expiresIn ?? 0) * 1000;
        const updated: AuthTokens = { ...data, expiresAt };
        setTokens(updated);
        storageSet(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      } catch (e) {
        // If refresh fails, log out
        setTokens(null);
        storageRemove(STORAGE_KEY);
        return null;
      } finally {
        refreshingRef.current = null;
      }
    })();
    refreshingRef.current = refreshPromise;
    return refreshPromise;
  };

  const authorizedFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    // First attempt with current/ensured token
    const firstTokens = await ensureValidToken();
    let headers = new Headers(init?.headers || {});
    if (firstTokens?.accessToken) {
      headers.set('Authorization', `${firstTokens.tokenType} ${firstTokens.accessToken}`);
    }
    let res = await fetch(input, { ...init, headers });
    if (res.status !== 401) {
      return res;
    }

    // On 401, try to refresh and retry once
    const refreshed = await ensureValidToken(true);
    if (!refreshed?.accessToken) {
      return res; // refresh failed; return original 401 (logout will be triggered in ensureValidToken)
    }
    headers = new Headers(init?.headers || {});
    headers.set('Authorization', `${refreshed.tokenType} ${refreshed.accessToken}`);
    res = await fetch(input, { ...init, headers });
    return res;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isInitializing,
        tokens,
        login,
        demoLogin,
        signup,
        confirmEmail,
        forgotPassword,
        confirmResetCode,
        logout,
        authHeader,
        authorizedFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
